import {
  isPageStale,
  subscribePageStale,
} from "@/lib/virtualspace/stale-deployment-store";

/**
 * Once a tab knows it is out of date, its next in-app link click loads the
 * page fresh instead of running a client-side navigation.
 *
 * Why: production ships many times a day (16 releases on 2026-10-14, about
 * one every 35–40 minutes in working hours), so a customer session of a few
 * minutes often spans one. PostHog replays 01a13a37-1cc2-79df-8e6d-859ffa4c2ae8
 * and 01a13ac1-a0fe-7ec2-9a56-c93f6b0b24fd: both tabs loaded minutes before a
 * release went live, a later action hit a server action the new deployment
 * no longer had, and the "This page is out of date" banner then stayed up
 * for the rest of the tab's life, because a client-side navigation never
 * reloads the document — it keeps the old JavaScript, and its RSC/chunk
 * requests can fail against the new deployment too. One replay shows the
 * customer stuck trying to open a capture under the banner.
 *
 * A full load fixes all of it at once: the new deployment's code, a page
 * that works, and no banner. So when the page is stale, a plain left click
 * on a same-origin link becomes `location.assign(href)`. Modified clicks
 * (new tab), downloads, other targets, in-page hash jumps, and cross-origin
 * links are left alone. Back/forward while stale reloads the page it lands
 * on for the same reason.
 *
 * Kept separate from `stale-deployment-guard.tsx` / `-watch.ts` / `-store.ts`
 * (owned by another change); it only reads the store.
 */

type NavigationTarget = Pick<
  Window,
  "addEventListener" | "removeEventListener" | "location"
>;

type ClickLike = Pick<
  MouseEvent,
  | "button"
  | "metaKey"
  | "ctrlKey"
  | "shiftKey"
  | "altKey"
  | "defaultPrevented"
  | "target"
  | "preventDefault"
  | "stopPropagation"
>;

/**
 * The URL a stale tab should hard-load for this click, or `null` to let the
 * click through unchanged.
 */
export function staleHardNavigationHref(
  event: ClickLike,
  currentHref: string,
): string | null {
  if (event.defaultPrevented) return null;
  if (event.button !== 0) return null;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;
  const start = event.target as Element | null;
  const anchor =
    start && typeof start.closest === "function"
      ? (start.closest("a[href]") as HTMLAnchorElement | null)
      : null;
  if (!anchor) return null;
  const target = anchor.getAttribute("target");
  if (target && target !== "_self") return null;
  if (anchor.hasAttribute("download")) return null;
  const rawHref = anchor.getAttribute("href") ?? "";
  if (rawHref.startsWith("#")) return null;
  let next: URL;
  let current: URL;
  try {
    current = new URL(currentHref);
    next = new URL(rawHref, current);
  } catch {
    return null;
  }
  if (next.protocol !== "http:" && next.protocol !== "https:") return null;
  if (next.origin !== current.origin) return null;
  // Same document, different fragment: an in-page jump, not a navigation.
  if (
    next.pathname === current.pathname &&
    next.search === current.search &&
    next.hash !== current.hash
  ) {
    return null;
  }
  return next.href;
}

/**
 * Install the stale-tab hard-navigation behaviour. Listens only while the
 * page is stale, in the capture phase on `window`, so it runs before React's
 * root listener (and `next/link`'s `router.push`).
 */
export function installStaleHardNavigation(
  target: NavigationTarget = window,
  isStale: () => boolean = isPageStale,
  subscribe: typeof subscribePageStale = subscribePageStale,
): () => void {
  let installed = false;

  const onClick = (event: Event) => {
    if (!isStale()) return;
    const mouse = event as MouseEvent;
    const href = staleHardNavigationHref(mouse, target.location.href);
    if (!href) return;
    mouse.preventDefault();
    mouse.stopPropagation();
    target.location.assign(href);
  };
  const onPopState = () => {
    if (isStale()) target.location.reload();
  };

  const sync = () => {
    if (installed || !isStale()) return;
    installed = true;
    target.addEventListener("click", onClick, true);
    target.addEventListener("popstate", onPopState);
  };

  sync();
  const unsubscribe = subscribe(sync);
  return () => {
    unsubscribe();
    if (installed) {
      target.removeEventListener("click", onClick, true);
      target.removeEventListener("popstate", onPopState);
      installed = false;
    }
  };
}
